// src/components/Admin/CommentManager/CommentsLogTable.jsx

import { useEffect, useState, useMemo } from 'react';
import {
    Box, TextField, Select, MenuItem, Typography,
    Table, TableHead, TableRow, TableCell, TableBody,
    TableContainer, Paper, CircularProgress, Pagination,
    Stack, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../../api/axios';

const actionLabel = {
    soft_delete: '삭제',
    blind: '블라인드',
    unblind: '블라인드 해제',
    edit: '수정',
};

const ITEMS_PER_PAGE = 15;

export default function CommentsLogTable() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ✅ 모바일(sm 이하)

    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    /* ───────── 데이터 로드 ───────── */
    useEffect(() => {
        api.get('/api/comments/logs/')
            .then((res) => setLogs(res.data.logs || []))
            .catch(() => alert('댓글 로그를 불러오는 데 실패했습니다.'))
            .finally(() => setLoading(false));
    }, []);

    /* ───────── 필터링 ───────── */
    const filtered = useMemo(() => (
        logs
            .filter((log) => (actionFilter === 'all' ? true : log.action === actionFilter))
            .filter((log) =>
                (log.comment_content || '').toLowerCase().includes(search.toLowerCase())
                || (log.post_title || '').toLowerCase().includes(search.toLowerCase())
                || (log.action || '').toLowerCase().includes(search.toLowerCase())
                || (log.memo || '').toLowerCase().includes(search.toLowerCase())
                || (log.nickname || '').toLowerCase().includes(search.toLowerCase())
            )
    ), [logs, search, actionFilter]);

    useEffect(() => setPage(1), [search, actionFilter]);

    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
    const paginated = filtered.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE,
    );

    /* ───────── 로딩 ───────── */
    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    /* ───────── 모바일 카드 렌더 ───────── */
    const MobileList = () => (
        <Stack spacing={1.25}>
            {paginated.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    🔍 조건에 맞는 로그가 없습니다.
                </Paper>
            ) : (
                paginated.map((log) => {
                    const act = actionLabel[log.action] || log.action;
                    const commentShort = (log.comment_content || '').length > 80
                        ? `${log.comment_content.slice(0, 80)}...`
                        : (log.comment_content || '-');

                    return (
                        <Paper key={log.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                            {/* 상단: ID / 행동 */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                    ID {log.id}
                                </Typography>
                                <Typography variant="caption" fontWeight={800}>
                                    {act}
                                </Typography>
                            </Stack>

                            {/* 게시글 */}
                            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }} noWrap>
                                <a
                                    href={`/main/posts/${log.post_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none', color: '#1976d2' }}
                                >
                                    {log.post_title || `게시글 #${log.post_id}`} (#{log.post_id})
                                </a>
                            </Typography>

                            {/* 댓글 내용 */}
                            <Typography
                                variant="body2"
                                sx={{ mt: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                            >
                                {commentShort}
                            </Typography>

                            {/* 메타 정보 */}
                            <Stack spacing={0.25} sx={{ mt: 1 }}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">작성자</Typography>
                                    <Typography variant="caption" fontWeight={700}>{log.nickname || '-'}</Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">처리자</Typography>
                                    <Typography variant="caption" fontWeight={700}>
                                        {log.acted_by?.trim() || '알 수 없음'}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">시간</Typography>
                                    <Typography variant="caption" fontWeight={700}>{log.acted_at || '-'}</Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">사유</Typography>
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        sx={{ ml: 1, textAlign: 'right', wordBreak: 'break-word' }}
                                    >
                                        {log.memo || '-'}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                    );
                })
            )}
        </Stack>
    );

    /* ───────── PC 테이블 렌더 ───────── */
    const DesktopTable = () => (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
                borderRadius: 2,
                '& tr:hover': { bgcolor: 'grey.100' },
                '& td, & th': {
                    borderBottom: '1px solid #eee',
                    p: 1.25,
                    whiteSpace: 'nowrap',
                },
                '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
            }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell align="center">ID</TableCell>
                        <TableCell align="left">댓글</TableCell>
                        <TableCell align="left">게시글</TableCell>
                        <TableCell align="left">작성자</TableCell>
                        <TableCell align="center">행동</TableCell>
                        <TableCell align="center">처리자</TableCell>
                        <TableCell align="center">시간</TableCell>
                        <TableCell align="center">사유</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {paginated.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                🔍 조건에 맞는 로그가 없습니다.
                            </TableCell>
                        </TableRow>
                    ) : (
                        paginated.map((log) => (
                            <TableRow key={log.id}>
                                <TableCell align="center">{log.id}</TableCell>
                                <TableCell align="left">
                                    {(log.comment_content || '').length > 40
                                        ? `${(log.comment_content || '').slice(0, 40)}...`
                                        : (log.comment_content || '-')}
                                </TableCell>
                                <TableCell align="left">
                                    <a
                                        href={`/main/posts/${log.post_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none', color: '#1976d2' }}
                                    >
                                        {log.post_title} (#{log.post_id})
                                    </a>
                                </TableCell>
                                <TableCell align="left">{log.nickname || '-'}</TableCell>
                                <TableCell align="center">{actionLabel[log.action] || log.action}</TableCell>
                                <TableCell align="center">{log.acted_by?.trim() || '알 수 없음'}</TableCell>
                                <TableCell align="center">{log.acted_at}</TableCell>
                                <TableCell align="center">{log.memo || '-'}</TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    /* ───────── 렌더 ───────── */
    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            {/* 검색·필터 바 */}
            <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={1.5}
                sx={{ mb: 2, alignItems: isMobile ? 'stretch' : 'center' }}
            >
                <TextField
                    label="댓글/게시글/작성자/사유"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth={isMobile}
                    sx={{ minWidth: isMobile ? '100%' : 240 }}
                />
                <Select
                    size="small"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    fullWidth={isMobile}
                    sx={{ minWidth: isMobile ? '100%' : 140 }}
                >
                    <MenuItem value="all">전체</MenuItem>
                    <MenuItem value="soft_delete">삭제</MenuItem>
                    <MenuItem value="blind">블라인드</MenuItem>
                    <MenuItem value="unblind">블라인드 해제</MenuItem>
                </Select>
                <Typography variant="body2" color="text.secondary">
                    총 {logs.length}개 / 현재 {filtered.length}개
                </Typography>
            </Stack>

            {/* 본문 */}
            {isMobile ? <MobileList /> : <DesktopTable />}

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        color="primary"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        size={isMobile ? 'small' : 'medium'}
                    />
                </Box>
            )}
        </Box>
    );
}
